import { gql, useApolloClient } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import Toast from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { CountBadgeWeb } from '@components/Modals/CountBadge.web';
import { useConfirmModal } from '@components/Modals/MobileModal';
import { NavItemRow } from '@components/Modals/NavItemRow.web';
import { NewChatModal, truncateText } from '@components/Modals/NewChatModal';
import { PendingInviteRow } from '@components/Modals/PendingInviteRow';
import { WebHoverBox } from '@components/Modals/WebHoverBox';
import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import {
  BottomToast,
  BottomToastHandle,
} from '@components/shared/BottomToast.web';
import { ListNavigationItemProps } from '@components/shared/ListNavigationItem/ListNavigationItem';
import { ContactRowSkeleton } from '@components/shared/skeletons/ContactRowSkeleton';
import { SkeletonList } from '@components/shared/skeletons/SkeletonList';
import {
  useCreateChatInviteMutation,
  useGetChatQuery,
  useLeaveChatMutation,
  useDeleteChatInvitationMutation,
  useDeleteUserRequestMutation,
  useResendChatInvitationMutation,
  User,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useTaskToast } from '@hooks/useTaskToast';
import theme from '@themes/theme';
import {
  CLIPBOARD_FAILURE_MESSAGE_KEYS,
  ClipboardFailureReason,
  copyTextToClipboard,
} from '@utils/copyTextToClipboard';

interface groupChatProps {
  chatId: string;
  showAddMembersModal: boolean;
  onClose: () => void;
}

const REMOVE_CHAT_MEMBER_MUTATION = gql`
  mutation RemoveChatMember($id: ID!, $userId: ID!) {
    removeChatMember(id: $id, userId: $userId)
  }
`;
const INACTIVE_MEMBER_ERROR_TEXT = 'not an active member of this chat';

const renderContactRowSkeleton = () => <ContactRowSkeleton />;

const isInactiveMemberError = (error: unknown): boolean => {
  const hasMatchingMessage = (errors?: Array<{ message?: string }>) =>
    Array.isArray(errors) &&
    errors.some((item) =>
      item?.message?.toLowerCase().includes(INACTIVE_MEMBER_ERROR_TEXT)
    );

  if (!error || typeof error !== 'object') return false;

  const apolloError = error as {
    graphQLErrors?: Array<{ message?: string }>;
    errors?: Array<{ message?: string }>;
  };

  return (
    hasMatchingMessage(apolloError.graphQLErrors) ||
    hasMatchingMessage(apolloError.errors)
  );
};

export const GroupChatMembers: React.FC<groupChatProps> = ({
  chatId,
  showAddMembersModal,
  onClose,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { me } = useMe();

  const [showAddNewMembersModal, setShowAddNewMemberModal] = useState(false);
  const [removeUserId, setRemoveUserId] = useState<User['id'] | undefined>(
    undefined
  );
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<User | undefined>(
    undefined
  );

  const { data, loading: chatLoading } = useGetChatQuery({
    variables: { id: chatId },
    skip: !chatId,
  });
  const [resendChatInvitation] = useResendChatInvitationMutation({});
  const [deleteChatInvitation] = useDeleteChatInvitationMutation();
  const [deleteUserRequest] = useDeleteUserRequestMutation();

  const { getChat: chat } = data || { chat: undefined };
  // TA-10279: `data`'s `loading` was previously dropped entirely — the
  // Members list rendered empty (no spinner, no skeleton) for the whole
  // first fetch. 150ms minimum hold per the global skeleton rule.
  const showMembersSkeleton = useMinimumVisible(
    chatLoading && (chat?.users?.length ?? 0) === 0
  );

  const userRequests =
    chat?.userRequests?.filter(
      (item) =>
        item.status !== 'rejected' &&
        item.status !== 'approved' &&
        item.status !== 'joined'
    ) ?? [];

  const ownerId = chat?.owner?.id;
  const isOwner = me?.id === ownerId;
  const { modal: confirmModal, showConfirm } = useConfirmModal();
  const memberName = memberToRemove?.name ?? '';
  const truncatedGroupName = truncateText(chat?.name ?? '', 50);
  const removeMemberConfirmMessageRaw = String(
    t('models:chat.removeMemberConfirmationMessage', {
      member: memberName,
      group: truncatedGroupName,
    })
  );
  const removeMemberConfirmMessageParts =
    memberName && removeMemberConfirmMessageRaw.includes(memberName)
      ? removeMemberConfirmMessageRaw.split(memberName)
      : [];

  const { toastRef, showToast } = useTaskToast();

  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [createChatInvite] = useCreateChatInviteMutation();
  // Same red-icon failure toast as MediaViewer's copy-link (TA-10845).
  const copyFailedToastRef = useRef<BottomToastHandle>(null);
  const showCopyFailedToast = (reason: ClipboardFailureReason) => {
    copyFailedToastRef.current?.show(
      t(CLIPBOARD_FAILURE_MESSAGE_KEYS[reason]),
      { duration: 3000, icon: 'warning' }
    );
  };

  const handleCopyInviteLink = async () => {
    // TA-10279: re-entrancy guard — same single-flight pattern as
    // Contact.tsx's own request-button guard. Previously nothing prevented
    // a second click from firing a concurrent createChatInvite.
    if (isCopyingLink) return;
    setIsCopyingLink(true);
    try {
      const result = await createChatInvite({ variables: { chatId } });
      const link = result.data?.createInvite;
      if (!link) {
        showCopyFailedToast('link-generation-failed');
        return;
      }
      const copyResult = await copyTextToClipboard(link);
      if (!copyResult.success) {
        showCopyFailedToast(copyResult.reason);
        return;
      }
      setIsLinkCopied(true);
      setTimeout(() => setIsLinkCopied(false), 3000);
    } catch {
      // TA-10279: previously swallowed silently, with no feedback at all —
      // "restore the button and show an inline error on failure".
      showCopyFailedToast('link-generation-failed');
    } finally {
      setIsCopyingLink(false);
    }
  };

  const [leaveChat, { error: leaveChatError }] = useLeaveChatMutation({
    refetchQueries: ['getChat'],
  });

  useEffect(() => {
    if (!leaveChatError) return;
    showToast(t('models:chat.leaveFailedTryAgain'), true);
  }, [leaveChatError]);

  const onRemoveMemberChat = async (userId: string) => {
    setRemoveUserId(userId);
    try {
      const result = await client.mutate({
        mutation: REMOVE_CHAT_MEMBER_MUTATION,
        variables: {
          id: chatId,
          userId,
        },
        refetchQueries: ['getChat', 'listMessages'],
        errorPolicy: 'all',
      });
      if (isInactiveMemberError(result)) {
        showToast(t('models:chat.removeMemberAlreadyLeftToast'));
      } else if (result.errors?.length) {
        showToast(t('shared:exceptionMsg'), true);
      }
    } catch (error) {
      if (isInactiveMemberError(error)) {
        showToast(t('models:chat.removeMemberAlreadyLeftToast'));
      } else {
        showToast(t('shared:exceptionMsg'), true);
      }
    } finally {
      setShowRemoveMemberModal(false);
      setMemberToRemove(undefined);
      setRemoveUserId(undefined);
    }
  };

  const handleLeaveChat = () => {
    leaveChat({
      variables: {
        id: chat?.id,
      },
    });
  };

  const confirmAndDeleteInvitation = (invitationId: string) => {
    deleteChatInvitation({
      variables: { chatInvitationId: invitationId },
      onCompleted: () => {
        showToast(t('models:chat.inviteDeleted'));
      },
      refetchQueries: ['getChat'],
      onError: () => {
        showToast(t('models:chat.deleteFailedTryAgain'), true);
      },
    });
  };

  const confirmAndDeleteUserRequest = (requestId: string) => {
    deleteUserRequest({
      variables: { id: requestId },
      onCompleted: () => {
        showToast(t('models:chat.inviteDeleted'));
      },
      refetchQueries: ['getChat'],
      onError: () => {
        showToast(t('models:chat.deleteFailedTryAgain'), true);
      },
    });
  };

  const copyLinkItemTitle = () => {
    if (isCopyingLink) return t('shared:copyingLink');
    if (isLinkCopied) return t('shared:linkCopied');
    return t('shared:copyLinkToInvite');
  };

  const copyLinkItem: ListNavigationItemProps = {
    title: copyLinkItemTitle(),
    iconName: isLinkCopied ? 'Check' : 'Link',
    onPress: handleCopyInviteLink,
    disabled: isCopyingLink,
  };
  const inviteLinkItem: ListNavigationItemProps = {
    title: t('models:chat.inviteOrAddMember'),
    iconName: 'UserPlus',
    onPress: () => {
      setShowAddNewMemberModal(true);
    },
  };
  const linkItems: ListNavigationItemProps[] = isOwner
    ? [inviteLinkItem, copyLinkItem]
    : [copyLinkItem];

  return (
    <>
      {confirmModal}
      <WebModal
        onClose={onClose}
        visible={showAddMembersModal}
        width={504}
        title={t('models:chat.chatMenuList.chatMembers')}
        height={582}>
        <>
          <Toast ref={toastRef} />
          <BottomToast ref={copyFailedToastRef} />
          <Box flex={1}>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ paddingBottom: 16 }}
              showsVerticalScrollIndicator={true}>
              {!(chat?.settings?.archivedAt ?? false) && (
                <Box mt='m'>
                  {linkItems.map((item) => {
                    return (
                      <NavItemRow
                        key={item.title}
                        title={item.title}
                        iconName={item.iconName}
                        onPress={item.onPress}
                        disabled={item.disabled}
                      />
                    );
                  })}
                </Box>
              )}
              {(chat?.pendingInvitations?.length ?? 0) +
                (userRequests.length ?? 0) >
                0 && (
                <Box mt='m'>
                  <Box mx='m' my='xs' flexDirection='row' alignItems='center'>
                    <Text variant='labelEmphasized' color='textPrimary'>
                      {t('models:chat.pendingMembers')}
                    </Text>
                    <CountBadgeWeb
                      count={(
                        (chat?.pendingInvitations?.length ?? 0) +
                        (userRequests.length ?? 0)
                      ).toString()}
                    />
                  </Box>

                  {chat!.pendingInvitations.map((item) => {
                    return (
                      <PendingInviteRow
                        key={item.id ?? item.email}
                        email={item.email}
                        expiredAt={item.expiredAt}
                        onResend={() => {
                          resendChatInvitation({
                            variables: {
                              chatInvitationId: item.id,
                            },
                            onCompleted: () => {
                              showToast(t('models:chat.invitationResent'));
                            },
                            refetchQueries: ['getChat'],
                            onError: () => {
                              showToast(
                                t('models:chat.resendFailedTryAgain'),
                                true
                              );
                            },
                          });
                        }}
                        onDelete={() => {
                          showConfirm({
                            title: t('models:chat.cancelInvite'),
                            message: t(
                              'models:chat.thisWillCancelThePendingInviteFor',
                              { email: item.email }
                            ),
                            doNotShowCancel: true,
                            confirmText: t('models:chat.cancelInvite'),
                            onConfirm: () =>
                              confirmAndDeleteInvitation(item.id),
                          });
                        }}
                      />
                    );
                  })}
                  {userRequests.map((item) => {
                    return (
                      <PendingInviteRow
                        key={item.id ?? item.id}
                        user={item.user}
                        email={item.user?.email ?? ''}
                        expiredAt={item.expiredAt}
                        onResend={() => {}}
                        onDelete={() => {
                          showConfirm({
                            title: t('models:chat.cancelInvite'),
                            message: t(
                              'models:chat.thisWillCancelThePendingInviteFor',
                              { email: item.user?.email }
                            ),
                            doNotShowCancel: true,
                            confirmText: t('models:chat.cancelInvite'),
                            onConfirm: () =>
                              confirmAndDeleteUserRequest(item.id),
                          });
                        }}
                      />
                    );
                  })}
                </Box>
              )}

              <Box>
                <Box mx='m' my='xs' flexDirection='row' alignItems='center'>
                  <Text variant='labelEmphasized' color='textPrimary'>
                    {t('shared:members')}
                  </Text>
                  <CountBadgeWeb
                    count={(chat?.users?.length ?? 0).toString()}
                  />
                </Box>

                {showMembersSkeleton && (
                  <SkeletonList count={3} row={renderContactRowSkeleton} />
                )}
                {!showMembersSkeleton &&
                  chat?.users?.map((user) => {
                    return (
                      <WebHoverBox
                        px='m'
                        key={user.id}
                        flexDirection='row'
                        height={60}
                        alignItems='center'>
                        <Avatar
                          size='medium'
                          avatarType='user'
                          id={user.id}
                          avatar={user.avatar}
                          label={user.name}
                          isMultiple={false}
                          disableAvatarPress={true}
                        />
                        <Box ml='xs' flex={1}>
                          <Text
                            variant='body'
                            flexShrink={1}
                            color='textPrimary'
                            numberOfLines={1}
                            ellipsizeMode='tail'>
                            {user.name}
                          </Text>
                          <Text
                            variant='webBodyHintSubline'
                            mt='xxs'
                            color='grey04'
                            flexShrink={1}
                            numberOfLines={1}
                            ellipsizeMode='tail'>
                            {user.email}
                          </Text>
                        </Box>
                        {ownerId === user.id && (
                          <Text
                            width={88}
                            variant='buttonLabel'
                            flexShrink={1}
                            color='textPrimary'
                            numberOfLines={1}
                            ellipsizeMode='tail'>
                            {t('models:profile.subscriptionPlans.admin')}
                          </Text>
                        )}
                        {isOwner && user.id !== me?.id && (
                          <TouchableOpacity
                            disabled={removeUserId === user.id}
                            onPress={() => {
                              setMemberToRemove(user);
                              setShowRemoveMemberModal(true);
                            }}>
                            <Box flexDirection='row' alignItems='center'>
                              {removeUserId === user.id && (
                                <ActivityIndicator
                                  style={{ height: 16 }}
                                  color={theme.colors.textPrimary}
                                />
                              )}
                              <Text
                                marginLeft='xs'
                                width={88}
                                variant='buttonLabel'
                                color='alertRed'
                                flexShrink={1}
                                numberOfLines={1}
                                ellipsizeMode='tail'>
                                {removeUserId === user.id
                                  ? t('models:chat.removingMember')
                                  : t('shared:remove')}
                              </Text>
                            </Box>
                          </TouchableOpacity>
                        )}
                        {!isOwner && user.id === me?.id && (
                          <TouchableOpacity
                            onPress={() => {
                              showConfirm({
                                title: t('models:chat.leaveGroup.cta'),
                                message: t('models:chat.leaveGroup.message'),
                                doNotShowCancel: true,
                                confirmText: t('models:chat.leaveGroup.cta'),
                                onConfirm: () => {
                                  handleLeaveChat();
                                },
                              });
                            }}>
                            <Text
                              width={88}
                              variant='buttonLabel'
                              color='alertRed'
                              flexShrink={1}
                              numberOfLines={1}
                              ellipsizeMode='tail'>
                              {t('shared:LeaveTask')}
                            </Text>
                          </TouchableOpacity>
                        )}
                      </WebHoverBox>
                    );
                  })}
              </Box>
            </ScrollView>
          </Box>
        </>

        {showAddNewMembersModal && (
          <NewChatModal
            chatId={chatId}
            showModal={showAddNewMembersModal}
            onClose={() => {
              setShowAddNewMemberModal(false);
            }}
          />
        )}
      </WebModal>
      <ConfirmModal
        showModal={showRemoveMemberModal}
        onClose={() => {
          if (!removeUserId) {
            setShowRemoveMemberModal(false);
            setMemberToRemove(undefined);
          }
        }}
        onPress={() => {
          if (memberToRemove?.id) onRemoveMemberChat(memberToRemove.id);
        }}
        buttonText={t('models:chat.removeMemberConfirmationButton')}
        cancelButtonText={t('shared:cancel')}
        confirmButtonBorderWidth={0}
        title={t('models:chat.removeMember')}
        message={
          <Text variant='bodySecondary'>
            {removeMemberConfirmMessageParts.length > 0 ? (
              <>
                {removeMemberConfirmMessageParts[0]}
                <Text variant='bodySecondary' color='greenSecondary'>
                  {memberName}
                </Text>
                {removeMemberConfirmMessageParts.slice(1).join(memberName)}
              </>
            ) : (
              removeMemberConfirmMessageRaw
            )}
          </Text>
        }
        isLoading={Boolean(removeUserId)}
      />
    </>
  );
};
